import { DataTable } from "@/components/data-table/DataTable";
import { DataTableColumnHeader } from "@/components/data-table/DataTableColumnHeader";
import { Form } from "@/components/form/form";
import { AdminFamiliesComboboxFormField } from "@/components/form/form-field/combobox/admin-families-combobox-form-field";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PortalPageHeader } from "@/components/portal-page-header";
import { findEnumName } from "@/lib/enum-utils";
import { positiveIdValidation } from "@/lib/form-validation";
import { buildDataTableQueryOptions, ensureRouteQueriesData } from "@/lib/route-loader";
import { dataTableFiltersSearchSchema } from "@/lib/route-search";
import {
  adminOrganizationFamilyGetAdminFamiliesForOrganizationList,
  adminOrganizationFamilyGetAdminFamilyFilterConfig,
  getAdminOrganizationFamilyGetAdminFamiliesForOrganizationListMutationOptions,
  getAdminOrganizationFamilyGetAdminFamilyFilterConfigQueryKey,
  getAdminOrganizationFamilyGetAdminFamilyFilterConfigQueryOptions,
  useAdminOrganizationFamilyAddFamilyToOrganization,
} from "@/orval/admin-organization-family";
import { OrganizationFamilyStandingStatus, type AdminFamilyListItemDTO } from "@/orval/model";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { PlusIcon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const familiesColumns: ColumnDef<AdminFamilyListItemDTO>[] = [
  {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
    accessorKey: "name",
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <span>{row.original.name}</span>
        {!row.original.isActive && <Badge variant="secondary">Inactive</Badge>}
      </div>
    ),
  },
  {
    header: "Standing",
    accessorFn: (row) => findEnumName(OrganizationFamilyStandingStatus, row.standingStatus),
    id: "standingStatus",
  },
  {
    header: "Email",
    accessorFn: (row) => row.emails.join(", "),
    id: "emails",
  },
  {
    header: "Phone",
    accessorFn: (row) => row.phones.join(", "),
    id: "phones",
  },
  {
    header: "Adults",
    accessorKey: "adultsCount",
  },
  {
    header: "Children",
    accessorKey: "childrenCount",
  },
];

export const Route = createFileRoute("/_app/organizations/$organizationId/admin/families")({
  validateSearch: dataTableFiltersSearchSchema,
  loader: ({ params, location, ...loaderContext }) => {
    const { searchString, filters } = dataTableFiltersSearchSchema.parse(location.search);
    const queryOptions = buildDataTableQueryOptions({ searchString });
    const listMutationOptions = getAdminOrganizationFamilyGetAdminFamiliesForOrganizationListMutationOptions();
    const listMutationFn = listMutationOptions.mutationFn;

    return ensureRouteQueriesData(loaderContext, [
      getAdminOrganizationFamilyGetAdminFamilyFilterConfigQueryOptions(params.organizationId),
      {
        queryKey: [...(listMutationOptions.mutationKey ?? []), params.organizationId, queryOptions, filters],
        queryFn: () => {
          if (!listMutationFn) {
            throw new Error("Generated families list mutation function is unavailable.");
          }

          return listMutationFn({
            organizationId: params.organizationId,
            data: filters ?? [],
            params: queryOptions,
          });
        },
      },
    ]);
  },
  component: FamiliesAdminPage,
});

function FamiliesAdminPage() {
  const { organizationId } = Route.useParams();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const getFamilies = adminOrganizationFamilyGetAdminFamiliesForOrganizationList;
  const getFamilyFiltersConfig = adminOrganizationFamilyGetAdminFamilyFilterConfig;
  const { mutateAsync: addFamily } = useAdminOrganizationFamilyAddFamilyToOrganization();

  const onAddSubmit = async (data: { familyId: number }) => {
    await addFamily({ organizationId, familyId: data.familyId });
    toast.success("Family successfully added to organization");
    setIsAddDialogOpen(false);
  };

  return (
    <div className="flex flex-1 min-h-0 flex-col p-4 md:p-6">
      <PortalPageHeader
        className="mb-8"
        title="Families"
        actions={
          <Button onClick={() => setIsAddDialogOpen(true)}>
            <PlusIcon className="size-4 mr-2" />
            Add Family
          </Button>
        }
      />

      <DataTable
        columns={familiesColumns}
        queryFn={(queryOptions, signal, filters) => getFamilies(organizationId, filters ?? [], queryOptions, signal)}
        queryKeyFn={(params, filters) => ["admin-families", organizationId, params, filters]}
        filtersQueryFn={(signal) => getFamilyFiltersConfig(organizationId, signal)}
        filtersQueryKeyFn={() => getAdminOrganizationFamilyGetAdminFamilyFilterConfigQueryKey(organizationId)}
        rowLink={(row) => ({
          from: Route.fullPath,
          to: "/organizations/$organizationId/admin/families/$familyId",
          params: {
            organizationId,
            familyId: row.id,
          },
          search: {
            tab: "payments",
          },
        })}
        getRowIdFn={(row) => row.id.toString()}
      />

      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Family</DialogTitle>
          </DialogHeader>
          <Form<{ familyId: number }>
            onSubmit={onAddSubmit}
            className="gap-y-4"
            useFormProps={{
              defaultValues: {
                familyId: 0,
              },
            }}
          >
            <AdminFamiliesComboboxFormField
              name="familyId"
              label="Family"
              organizationId={organizationId}
              description="Select a family from a user already in your organization but who's family has not been added yet."
              rules={positiveIdValidation("family")}
            />
            <DialogFooter className="flex-col sm:flex-row">
              <Button type="submit">Add Family</Button>
              <Button type="button" variant="secondary" onClick={() => setIsAddDialogOpen(false)}>
                Cancel
              </Button>
            </DialogFooter>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
